import { Phone, Mail, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { CLINIC, SERVICES } from "../data/clinic";
import { LOGO_DARK } from "../data/assets";
import { FacebookIcon, InstagramIcon, WhatsAppIcon } from "./SocialIcons";

const SOCIAL_LINKS = {
  facebook: "https://www.facebook.com/share/1DhdZ4gFkh/?mibextid=wwXIfr",
  instagram:
    "https://www.instagram.com/litisha_foot_care?igsh=MW4zaXFhNzAxcGtraQ%3D%3D&utm_source=qr",
  whatsapp: "https://wa.me/447547173220",
};

export default function Footer() {
  const goTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <footer data-testid="site-footer" className="relative pt-20 pb-10 border-t border-[#4A2D7A] bg-[#0E0524]">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#2BB8B4]/60 to-transparent" />

      <div className="lfc-container relative">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="grid place-items-center w-14 h-14 rounded-2xl bg-[#2D1456] ring-1 ring-[#2BB8B4]/40 p-1.5">
                <img src={LOGO_DARK} alt="Litisha Foot Care" className="w-full h-full object-contain" />
              </span>
              <div className="leading-tight">
                <div className="text-[16px] font-semibold text-white" style={{ fontFamily: "Outfit" }}>
                  Litisha Foot Care
                </div>
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#5FE6E0]">
                  Mobile Clinic · Scotland
                </div>
              </div>
            </div>
            <p className="mt-5 text-[15px] text-[#C2B4E6] leading-relaxed max-w-md">
              Professional, compassionate and personalised foot care delivered to the comfort
              of your own home. Qualified Foot Health Practitioners, fully insured.
            </p>

            <div className="mt-6 flex items-center gap-3">
              <a
                href={SOCIAL_LINKS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit Litisha Foot Care on Facebook"
                data-testid="footer-social-facebook"
                className="grid place-items-center w-11 h-11 rounded-full bg-white hover:scale-105 hover:-translate-y-0.5 transition-all duration-300 shadow-sm"
              >
                <FacebookIcon size={22} />
              </a>
              <a
                href={SOCIAL_LINKS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit Litisha Foot Care on Instagram"
                data-testid="footer-social-instagram"
                className="grid place-items-center w-11 h-11 rounded-full hover:scale-105 hover:-translate-y-0.5 transition-all duration-300 shadow-sm"
              >
                <InstagramIcon size={26} />
              </a>
              <a
                href={SOCIAL_LINKS.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Message Litisha Foot Care on WhatsApp"
                data-testid="footer-social-whatsapp"
                className="grid place-items-center w-11 h-11 rounded-full bg-white hover:scale-105 hover:-translate-y-0.5 transition-all duration-300 shadow-sm"
              >
                <WhatsAppIcon size={22} />
              </a>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#5FE6E0] font-medium mb-5">
              Services
            </div>
            <ul className="space-y-2.5">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.id}>
                  <button
                    onClick={() => goTo("services")}
                    data-testid={`footer-service-${s.id}`}
                    className="text-[14.5px] text-[#C2B4E6] hover:text-white transition text-left"
                  >
                    {s.title}
                  </button>
                </li>
              ))}
              <li>
                <button
                  onClick={() => goTo("services")}
                  className="text-[14px] text-[#2BB8B4] hover:text-white transition"
                >
                  View all services →
                </button>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-4">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#5FE6E0] font-medium mb-5">
              Get in touch
            </div>
            <ul className="space-y-4">
              <li>
                <a
                  href={`tel:${CLINIC.phoneRaw}`}
                  data-testid="footer-phone"
                  className="flex items-center gap-3 text-[15px] text-[#E5DEFA] hover:text-white transition"
                >
                  <Phone className="w-4 h-4 text-[#2BB8B4]" strokeWidth={1.6} />
                  {CLINIC.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${CLINIC.email}`}
                  data-testid="footer-email"
                  className="flex items-center gap-3 text-[15px] text-[#E5DEFA] hover:text-white transition"
                >
                  <Mail className="w-4 h-4 text-[#2BB8B4]" strokeWidth={1.6} />
                  {CLINIC.email}
                </a>
              </li>
              <li className="flex items-start gap-3 text-[15px] text-[#C2B4E6]">
                <MapPin className="w-4 h-4 text-[#2BB8B4] mt-1" strokeWidth={1.6} />
                <span>
                  {CLINIC.areas.join(" · ")}
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-[#4A2D7A]/60 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-[13px] text-[#8B7FB5]">
            © {new Date().getFullYear()} {CLINIC.name}. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px]">
            <Link
              to="/privacy"
              data-testid="footer-privacy-link"
              className="text-[#C2B4E6] hover:text-white transition-colors"
              onClick={() => window.scrollTo({ top: 0 })}
            >
              Privacy Policy
            </Link>
            <span className="text-[#4A2D7A]">·</span>
            <Link
              to="/privacy#cookies"
              data-testid="footer-cookies-link"
              className="text-[#C2B4E6] hover:text-white transition-colors"
              onClick={() => window.scrollTo({ top: 0 })}
            >
              Cookies
            </Link>
            <span className="text-[#4A2D7A]">·</span>
            <span className="text-[#8B7FB5]">
              Qualified Foot Health Practitioner · Fully insured
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
